/**
 * Swimlane Configuration
 * 
 * Defines all available swimlane grouping options for the Kanban view.
 * This config-driven approach makes it easy to add new grouping options
 * as new attributes are added to missions.
 * 
 * To add a new swimlane option:
 * 1. Add a new entry to SWIMLANE_OPTIONS
 * 2. Implement the getValue function to extract the grouping key from a task
 * 3. Optionally implement getLabel, getColor, and getOrder for display customization
 */

/**
 * Swimlane option definitions
 * 
 * Each option has:
 * - id: unique identifier (stored in settings)
 * - label: display name in dropdown
 * - icon: SVG icon component name (optional)
 * - getValue: function to extract grouping key from a task
 * - getLabel: function to get display label for a swimlane (optional, defaults to value)
 * - getColor: function to get color for a swimlane (optional)
 * - getOrder: function to determine swimlane order (optional, defaults to alphabetical)
 * - getEmptyLabel: label for tasks without a value (optional)
 * - available: function to check if this option is available (for future-proofing)
 */
export const SWIMLANE_OPTIONS = [
  {
    id: 'none',
    label: 'No Grouping',
    description: 'Show all cards in a flat list',
    getValue: () => null, // No grouping
    available: () => true,
  },
  {
    id: 'tag',
    label: 'Primary Tag',
    description: 'Group by primary tag',
    getValue: (task, context) => {
      // Get primary tag (first tag in array)
      const tags = task.tags || task.category;
      if (Array.isArray(tags) && tags.length > 0) {
        return tags[0];
      }
      if (typeof tags === 'string' && tags) {
        return tags;
      }
      return null;
    },
    getLabel: (value) => value || 'Untagged',
    getColor: (value, context) => {
      if (!value || !context?.allTags) return null;
      const tag = context.allTags.find(t => t.name === value);
      return tag?.color || null;
    },
    getOrder: (value, context) => {
      // Order by tag position in allTags, untagged at end
      if (!value) return 9999;
      if (!context?.allTags) return 0;
      const index = context.allTags.findIndex(t => t.name === value);
      return index >= 0 ? index : 9998;
    },
    getEmptyLabel: 'Untagged',
    available: () => true,
  },
  {
    id: 'priority',
    label: 'Priority',
    description: 'Group by priority level',
    getValue: (task) => task.priority || null,
    getLabel: (value) => {
      const labels = { high: 'High Priority', medium: 'Medium Priority', low: 'Low Priority' };
      return labels[value] || 'No Priority';
    },
    getColor: (value) => {
      const colors = { high: '#ef4444', medium: '#f59e0b', low: '#22c55e' };
      return colors[value] || null;
    },
    getOrder: (value) => {
      const order = { high: 0, medium: 1, low: 2 };
      return order[value] ?? 3;
    },
    getEmptyLabel: 'No Priority',
    // Priority field is now always available
    available: () => true,
  },
  {
    id: 'difficulty',
    label: 'Difficulty',
    description: 'Group by difficulty level',
    getValue: (task) => task.difficulty || null,
    getLabel: (value) => {
      const labels = { 
        very_easy: 'Very Easy', 
        easy: 'Easy', 
        medium: 'Medium', 
        hard: 'Hard', 
        extreme: 'Extreme' 
      };
      return labels[value] || 'No Difficulty';
    },
    getColor: (value) => {
      const colors = { 
        very_easy: '#22c55e', 
        easy: '#84cc16', 
        medium: '#f59e0b', 
        hard: '#f97316', 
        extreme: '#ef4444' 
      };
      return colors[value] || null;
    },
    getOrder: (value) => {
      const order = { very_easy: 0, easy: 1, medium: 2, hard: 3, extreme: 4 };
      return order[value] ?? 5;
    },
    getEmptyLabel: 'No Difficulty',
    available: () => true,
  },
  {
    id: 'assignee',
    label: 'Assignee',
    description: 'Group by assigned person',
    getValue: (task) => task.assignee || task.assigneeId || null,
    getLabel: (value, context) => {
      if (!value) return 'Unassigned';
      // Look up user name from context if available
      const user = context?.users?.find(u => u.id === value);
      return user?.name || value;
    },
    getColor: (value, context) => {
      // Could return user's avatar color
      const user = context?.users?.find(u => u.id === value);
      return user?.color || null;
    },
    getEmptyLabel: 'Unassigned',
    // Only available when assignee field exists (teams feature)
    available: (context) => context?.hasAssigneeField ?? false,
  },
  {
    id: 'dueDate',
    label: 'Due Date',
    description: 'Group by due date range',
    getValue: (task) => {
      if (!task.dueDate) return null;
      const due = new Date(task.dueDate);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
      
      if (due < today) return 'overdue';
      if (due <= weekFromNow) return 'thisWeek';
      return 'later';
    },
    getLabel: (value) => {
      const labels = { overdue: '⚠️ Overdue', thisWeek: 'This Week', later: 'Later' };
      return labels[value] || 'No Due Date';
    },
    getColor: (value) => {
      const colors = { overdue: '#ef4444', thisWeek: '#f59e0b', later: '#6b7280' };
      return colors[value] || null;
    },
    getOrder: (value) => {
      const order = { overdue: 0, thisWeek: 1, later: 2 };
      return order[value] ?? 3;
    },
    getEmptyLabel: 'No Due Date',
    // Only available when dueDate field exists
    available: (context) => context?.hasDueDateField ?? false,
  },
];

/**
 * Get available swimlane options based on current context
 * (filters out options that aren't available yet)
 */
export const getAvailableSwimlaneOptions = (context = {}) => {
  return SWIMLANE_OPTIONS.filter(option => option.available(context));
};

/**
 * Get a swimlane option by ID
 */
export const getSwimlaneOption = (id) => {
  return SWIMLANE_OPTIONS.find(option => option.id === id) || SWIMLANE_OPTIONS[0];
};

/**
 * Default swimlane setting
 */
export const DEFAULT_SWIMLANE_SETTING = 'none';

/**
 * Group tasks into swimlanes based on the selected option
 * 
 * @param {Array} tasks - Array of task objects
 * @param {string} swimlaneId - ID of the swimlane option to use
 * @param {Object} context - Additional context (allTags, users, etc.)
 * @returns {Array} Array of swimlane objects: { id, label, color, tasks }
 */
export const groupTasksIntoSwimlanes = (tasks, swimlaneId, context = {}) => {
  const option = getSwimlaneOption(swimlaneId);
  
  // No grouping - return null to indicate flat list
  if (option.id === 'none') {
    return null;
  }
  
  // Group tasks by value
  const groups = new Map();
  const emptyKey = '__empty__';
  
  tasks.forEach(task => {
    const value = option.getValue(task, context);
    const key = value ?? emptyKey;
    
    if (!groups.has(key)) {
      groups.set(key, {
        id: key,
        value: value,
        label: option.getLabel ? option.getLabel(value, context) : (value || option.getEmptyLabel || 'Other'),
        color: option.getColor ? option.getColor(value, context) : null,
        order: option.getOrder ? option.getOrder(value, context) : 0,
        tasks: [],
      });
    }
    
    groups.get(key).tasks.push(task);
  });
  
  // Convert to array and sort by order
  const swimlanes = Array.from(groups.values());
  swimlanes.sort((a, b) => a.order - b.order);
  
  return swimlanes;
};

export default {
  SWIMLANE_OPTIONS,
  getAvailableSwimlaneOptions,
  getSwimlaneOption,
  DEFAULT_SWIMLANE_SETTING,
  groupTasksIntoSwimlanes,
};

